import React, { useEffect, useId } from 'react';

import type { DrawReactComponentProps } from '@aline/common';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import clsx from 'clsx';
import { Queries } from 'src/editor/queries';
import { getAngleAtPoint, snapToClosestDegree } from 'src/helpers';
import { useAuthorizedTransforms, useOverrideGlobalCursor } from 'src/hooks';
import { useEditor, usePageEl } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

const RESIZER_SIZE = 10;
const HANDLE_OFFSET = 25;
const SNAP_THRESHOLD_DEGREES = 3;
const SNAP_VALUES = [0, 90, 180, 270, 360];
const ROTATE_CURSOR = 'crosshair';

// Negative so it sits at the center of the resize border.
const RESIZER_OFFSET = -(RESIZER_SIZE / 2);
const CONNECTOR_LINE_HEIGHT = HANDLE_OFFSET - RESIZER_SIZE;

const resizeHandlePositions = [
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
  'topMiddle',
  'bottomMiddle',
  'middleLeft',
  'middleRight',
] as const;

type ResizerPosition = (typeof resizeHandlePositions)[number];

interface ImageResizeState {
  boxPosition: { translateX: number; translateY: number };
  dimensions: { height: number; width: number };
  initialClick: { x: number; y: number };
  isRotating: boolean;
  resizerPos: ResizerPosition;
  resizingDrawingId: string | null;
  rotation: number;
}

const imageResizeStateVar = makeVar<ImageResizeState>({
  boxPosition: { translateX: 0, translateY: 0 },
  dimensions: { height: 0, width: 0 },
  initialClick: { x: 0, y: 0 },
  isRotating: false,
  resizerPos: 'middleRight',
  resizingDrawingId: null,
  rotation: 0,
});

interface StyleProps {
  rotation: number;
}

const useStyles = makeStyles<StyleProps>()((theme, { rotation }) => ({
  connectorLine: {
    backgroundColor: theme.palette.primary.main,
    height: CONNECTOR_LINE_HEIGHT,
    left: '50%',
    position: 'absolute',
    top: -CONNECTOR_LINE_HEIGHT,
    transform: 'translateX(-50%)',
    width: 1,
  },
  resizeBox: {
    border: `1px ${theme.palette.primary.main} solid`,
    boxSizing: 'border-box',
    cursor: 'default',
    position: 'absolute',
    rotate: `${rotation}deg`,
    zIndex: theme.appZIndex.imageResizer,
  },
  rotationHandle: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    cursor: ROTATE_CURSOR,
    height: RESIZER_SIZE,
    left: '50%',
    position: 'absolute',
    top: -HANDLE_OFFSET,
    transform: 'translateX(-50%)',
    width: RESIZER_SIZE,
  },
}));

export function ImageResizer({
  centerX,
  centerY,
  drawingEntry: [drawing, path],
}: DrawReactComponentProps['ImageResizer']): JSX.Element {
  const drawingId = useId();
  const editor = useEditor();
  const state = useReactiveVar(imageResizeStateVar);
  const pageEl = usePageEl();
  const { classes } = useStyles({ rotation: state.rotation });
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { rotation } = Queries.getDrawingVisualProperties(drawing);
  const isResizing = state.resizingDrawingId === drawingId;

  useOverrideGlobalCursor(
    isResizing
      ? state.isRotating
        ? ROTATE_CURSOR
        : getResizeHandleCursor(state.resizerPos, rotation)
      : null
  );

  useEffect(() => {
    const { containerHeightPx, containerWidthPx, imgHeightPx, imgWidthPx } =
      Queries.getDrawingDimensions(drawing);
    const { translateX, translateY } = calculateResizeBoxTranslation({
      containerHeightPx,
      containerWidthPx,
      imgHeightPx,
      imgWidthPx,
      resizeBox: { heightPx: imgHeightPx, widthPx: imgWidthPx },
      rotation,
    });

    // Ensures state.dimensions is synced even if a user undo/redo image size change.
    setImageState({
      boxPosition: { translateX, translateY },
      dimensions: { height: imgHeightPx, width: imgWidthPx },
      rotation,
    });
  }, [drawing, rotation, state.resizerPos]);

  useEffect(() => {
    function handleMouseUp(event: MouseEvent) {
      const state = imageResizeStateVar();
      if (state.resizingDrawingId !== drawingId) return;

      event.preventDefault();

      AuthorizedTransforms.setDrawingDimensions(editor, {
        at: path,
        props: {
          height: state.dimensions.height,
          rotation: state.rotation,
          width: state.dimensions.width,
        },
      });

      setImageState({
        initialClick: { x: 0, y: 0 },
        isRotating: false,
        resizingDrawingId: null,
      });
    }

    function handleMouseMove(event: MouseEvent) {
      if (!pageEl) return;

      const state = imageResizeStateVar();
      if (state.resizingDrawingId !== drawingId) return;

      event.preventDefault();

      const { containerHeightPx, containerWidthPx, imgHeightPx, imgWidthPx } =
        Queries.getDrawingDimensions(drawing);

      if (state.isRotating) {
        const { x: pageX, y: pageY } = pageEl.getBoundingClientRect();

        const drawingCenterPointRelativeToViewport = {
          x: pageX + centerX,
          y: pageY + centerY,
        };

        const degree = snapToClosestDegree({
          degree: getAngleAtPoint({
            x: event.clientY - drawingCenterPointRelativeToViewport.y,
            y: event.clientX - drawingCenterPointRelativeToViewport.x,
          }),
          snapThreshold: SNAP_THRESHOLD_DEGREES,
          // rotation is the existing value so it also snaps back to it.
          snapValues: [...SNAP_VALUES, rotation],
        });

        return setImageState({ rotation: degree });
      }

      const { height: newHeightPx, width: newWidthPx } = calculateResizeBoxDimensions({
        deltaX: event.clientX - state.initialClick.x,
        deltaY: event.clientY - state.initialClick.y,
        height: imgHeightPx,
        minHeight: 20,
        minWidth: 60,
        resizerPos: state.resizerPos,
        rotation,
        width: imgWidthPx,
      });

      const { translateX, translateY } = calculateResizeBoxTranslation({
        containerHeightPx,
        containerWidthPx,
        imgHeightPx,
        imgWidthPx,
        resizeBox: { heightPx: newHeightPx, widthPx: newWidthPx },
        rotation,
      });

      setImageState({
        boxPosition: { translateX, translateY },
        dimensions: {
          height: newHeightPx,
          width: newWidthPx,
        },
      });
    }

    window.addEventListener('mouseup', handleMouseUp, { capture: true });
    window.addEventListener('mousemove', handleMouseMove, { capture: true });

    return () => {
      window.removeEventListener('mouseup', handleMouseUp, { capture: true });
      window.removeEventListener('mousemove', handleMouseMove, { capture: true });
    };
  }, [AuthorizedTransforms, centerX, centerY, drawing, drawingId, editor, pageEl, path, rotation]);

  return (
    <div
      className={classes.resizeBox}
      style={{
        left: state.boxPosition.translateX,
        // minWidth/height seems to work better with rotation vs. plain width/height
        minHeight: state.dimensions.height,
        minWidth: state.dimensions.width,
        top: state.boxPosition.translateY,
      }}
      // Ensures mouseDown for Page/Header/Footer selection handlers don't fire when resizer is active
      onMouseDown={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div
        className={classes.rotationHandle}
        onMouseDown={() => {
          setImageState({
            isRotating: true,
            resizingDrawingId: drawingId,
          });
        }}
      />

      <div className={classes.connectorLine} />

      {resizeHandlePositions.map(resizerPos => {
        return (
          <ResizeImageHandle
            key={resizerPos}
            drawingId={drawingId}
            resizerPos={resizerPos}
            rotation={rotation}
          />
        );
      })}
    </div>
  );
}

const useResizeHandleStyles = makeStyles()(theme => ({
  bottomLeft: {
    bottom: RESIZER_OFFSET,
    left: RESIZER_OFFSET,
  },
  bottomMiddle: {
    bottom: RESIZER_OFFSET,
    left: '50%',
    transform: 'translateX(-50%)',
  },
  bottomRight: {
    bottom: RESIZER_OFFSET,
    right: RESIZER_OFFSET,
  },
  middleLeft: {
    left: RESIZER_OFFSET,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  middleRight: {
    right: RESIZER_OFFSET,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  resizer: {
    background: theme.palette.primary.main,
    border: `1px white solid`,
    boxSizing: 'border-box',
    cursor: 'move',
    height: RESIZER_SIZE,
    position: 'absolute',
    width: RESIZER_SIZE,
  },
  topLeft: {
    left: RESIZER_OFFSET,
    top: RESIZER_OFFSET,
  },
  topMiddle: {
    left: '50%',
    top: RESIZER_OFFSET,
    transform: 'translateX(-50%)',
  },
  topRight: {
    right: RESIZER_OFFSET,
    top: RESIZER_OFFSET,
  },
}));

function ResizeImageHandle({
  drawingId,
  resizerPos,
  rotation,
}: {
  drawingId: string;
  resizerPos: ResizerPosition;
  rotation: number;
}): JSX.Element {
  const { classes } = useResizeHandleStyles();

  return (
    <div
      key={resizerPos}
      className={clsx(classes.resizer, classes[resizerPos])}
      style={{ cursor: getResizeHandleCursor(resizerPos, rotation) }}
      onMouseDown={event => {
        event.preventDefault();
        event.stopPropagation();

        setImageState({
          initialClick: { x: event.clientX, y: event.clientY },
          resizerPos,
          resizingDrawingId: drawingId,
        });
      }}
    />
  );
}

function setImageState(newState: Partial<ImageResizeState>): void {
  const state = imageResizeStateVar();
  imageResizeStateVar({ ...state, ...newState });
}

type ResizeCursorDegree = 0 | 45 | 90 | 135 | 180 | 225 | 270 | 315;

function getResizeHandleCursor(resizerPos: ResizerPosition, rotation: number): string {
  const degreeToCursor: Record<ResizeCursorDegree, string> = {
    0: 'ns-resize',
    45: 'nesw-resize',
    90: 'ew-resize',
    135: 'nwse-resize',
    180: 'ns-resize',
    225: 'nesw-resize',
    270: 'ew-resize',
    315: 'nwse-resize',
  };

  return degreeToCursor[getResizeCursorDegree(getAdjustedRotation(resizerPos, rotation))];
}

function getAdjustedRotation(resizerPos: ResizerPosition, rotation: number): number {
  switch (resizerPos) {
    case 'topMiddle':
      return rotation;
    case 'topRight':
      return rotation + 45;
    case 'middleRight':
      return rotation + 90;
    case 'bottomRight':
      return rotation + 135;
    case 'bottomMiddle':
      return rotation + 180;
    case 'bottomLeft':
      return rotation + 225;
    case 'middleLeft':
      return rotation + 270;
    case 'topLeft':
      return rotation + 315;
  }
}

function getResizeCursorDegree(rotation: number): ResizeCursorDegree {
  const normalizedAngle = rotation % 360;
  const adjustedRotation = normalizedAngle < 0 ? normalizedAngle + 360 : normalizedAngle;

  if (adjustedRotation >= 0 && adjustedRotation < 22.5) return 0;
  if (adjustedRotation >= 45 - 22.5 && adjustedRotation < 45 + 22.5) return 45;
  if (adjustedRotation >= 90 - 22.5 && adjustedRotation < 90 + 22.5) return 90;
  if (adjustedRotation >= 135 - 22.5 && adjustedRotation < 135 + 22.5) return 135;
  if (adjustedRotation >= 180 - 22.5 && adjustedRotation < 180 + 22.5) return 180;
  if (adjustedRotation >= 225 - 22.5 && adjustedRotation < 225 + 22.5) return 225;
  if (adjustedRotation >= 270 - 22.5 && adjustedRotation < 270 + 22.5) return 270;

  return 315;
}

/**
 * Calculate the new dimensions of an image, and position of resize box,
 * based on the resizer position and the delta of the mouse movement.
 */
function calculateResizeBoxDimensions({
  deltaX,
  deltaY,
  height,
  minHeight = 0,
  minWidth = 0,
  resizerPos,
  rotation,
  width,
}: {
  deltaX: number;
  deltaY: number;
  height: number;
  resizerPos: ResizerPosition;
  rotation: number;
  width: number;
  minHeight?: number;
  minWidth?: number;
}): { height: number; width: number } {
  const rotationInRadians = (rotation * Math.PI) / 180;
  const cosTheta = Math.cos(rotationInRadians);
  const sinTheta = Math.sin(rotationInRadians);
  const rotatedDeltaX = deltaX * cosTheta + deltaY * sinTheta;
  const rotatedDeltaY = -deltaX * sinTheta + deltaY * cosTheta;

  switch (resizerPos) {
    case 'topLeft':
      return {
        height: Math.max(minHeight, height - rotatedDeltaY),
        width: Math.max(minWidth, width - rotatedDeltaX),
      };
    case 'topMiddle':
      return {
        height: Math.max(minHeight, height - rotatedDeltaY),
        width: Math.max(minWidth, width),
      };
    case 'topRight':
      return {
        height: Math.max(minHeight, height - rotatedDeltaY),
        width: Math.max(minWidth, width + rotatedDeltaX),
      };
    case 'middleLeft':
      return {
        height: Math.max(minHeight, height),
        width: Math.max(minWidth, width - rotatedDeltaX),
      };
    case 'middleRight':
      return {
        height: Math.max(minHeight, height),
        width: Math.max(minWidth, width + rotatedDeltaX),
      };
    case 'bottomLeft':
      return {
        height: Math.max(minHeight, height + rotatedDeltaY),
        width: Math.max(minWidth, width - rotatedDeltaX),
      };
    case 'bottomMiddle':
      return {
        height: Math.max(minHeight, height + rotatedDeltaY),
        width: Math.max(minWidth, width),
      };
    case 'bottomRight':
      return {
        height: Math.max(minHeight, height + rotatedDeltaY),
        width: Math.max(minWidth, width + rotatedDeltaX),
      };
  }
}

export function calculateResizeBoxTranslation({
  containerHeightPx,
  containerWidthPx,
  imgHeightPx,
  imgWidthPx,
  resizeBox,
  rotation,
}: {
  containerHeightPx: number;
  containerWidthPx: number;
  imgHeightPx: number;
  imgWidthPx: number;
  resizeBox: { heightPx: number; widthPx: number };
  rotation: number;
}): { rotationInRadians: number; translateX: number; translateY: number } {
  const rotationInRadians = (rotation * Math.PI) / 180;
  const cosTheta = Math.cos(rotationInRadians);
  const sinTheta = Math.sin(rotationInRadians);
  const widthDiff = (imgWidthPx - resizeBox.widthPx) / 2;
  const heightDiff = (imgHeightPx - resizeBox.heightPx) / 2;
  const xAdj = widthDiff * cosTheta + heightDiff * sinTheta;
  const yAdj = widthDiff * sinTheta + heightDiff * cosTheta;

  return {
    rotationInRadians,
    translateX: (containerWidthPx - resizeBox.widthPx) / 2 - xAdj,
    translateY: (containerHeightPx - resizeBox.heightPx) / 2 - yAdj,
  };
}
